/**
 * Un profilo di compenso nuovo: gli scaglioni per mese di abbonamento, con la scala disegnata mentre si scrive. Parte
 * dall'esempio di Romano Paolicelli (10% · 7,5% · 5% per sempre); i problemi li dice la stessa regola del server
 * (`validaProfilo`), prima di salvare.
 *
 * I campi tengono il TESTO scritto (con la virgola, in italiano) e lo leggono come numero solo per controllare e
 * salvare: un campo numerico che si riscrive a ogni tasto cancellerebbe la virgola mentre la si digita.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, Stack, TextField, Tooltip,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { SCAGLIONI_ESEMPIO, validaProfilo, type ScaglioneCompenso } from '@shared/partner/compensoPartner';
import { creaProfilo } from '../../services/partnerConsoleService';
import { useFormati } from './formati';
import { ScalaCompenso } from './ScalaCompenso';

interface Props {
  aperto: boolean;
  onChiudi: () => void;
  onSalvato: () => void;
  onErrore: (messaggio: string) => void;
}

interface Riga {
  dal: string;
  al: string;
  percentuale: string;
  perSempre: boolean;
}

export const DialogoProfilo: React.FC<Props> = ({ aperto, onChiudi, onSalvato, onErrore }) => {
  const { t } = useTranslation('partner');
  const f = useFormati();
  const [nome, setNome] = useState('');
  const [righe, setRighe] = useState<Riga[]>([]);
  const [lavora, setLavora] = useState(false);

  useEffect(() => {
    if (!aperto) return;
    setNome('');
    setRighe(SCAGLIONI_ESEMPIO.map((s) => ({
      dal: String(s.dal_mese), al: s.al_mese === null ? '' : String(s.al_mese), percentuale: f.numeroPerCampo(s.percentuale), perSempre: s.al_mese === null,
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aperto]);

  const scaglioni: ScaglioneCompenso[] = useMemo(() => righe.map((r) => ({
    dal_mese: f.leggiNumero(r.dal),
    al_mese: r.perSempre ? null : f.leggiNumero(r.al),
    percentuale: f.leggiNumero(r.percentuale),
  })), [righe, f]);
  const problemi = useMemo(() => validaProfilo({ nome, base: 'posti', scaglioni }), [nome, scaglioni]);
  const scalaValida = problemi.every((p) => p.codice === 'nome_mancante');

  const cambia = (i: number, campo: keyof Riga, valore: string | boolean) =>
    setRighe((xs) => xs.map((r, j) => (j === i ? { ...r, [campo]: valore } : r)));

  const aggiungi = () => setRighe((xs) => {
    const ultima = xs[xs.length - 1];
    const dal = ultima ? f.leggiNumero(ultima.dal) : 1;
    const fine = ultima && !ultima.perSempre ? f.leggiNumero(ultima.al) : dal + 11;
    const chiuse = ultima?.perSempre ? [...xs.slice(0, -1), { ...ultima, perSempre: false, al: String(fine) }] : xs;
    return [...chiuse, { dal: String(fine + 1), al: '', percentuale: ultima?.percentuale ?? '', perSempre: true }];
  });

  const salva = async () => {
    setLavora(true);
    try {
      await creaProfilo({ nome: nome.trim(), base: 'posti', scaglioni });
      onSalvato();
      onChiudi();
    } catch (e) {
      onErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setLavora(false);
    }
  };

  return (
    <Dialog open={aperto} onClose={lavora ? undefined : onChiudi} fullWidth maxWidth="sm">
      <DialogTitle>{t('dialogo_profilo.titolo')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label={t('dialogo_profilo.nome')} helperText={t('dialogo_profilo.nome_aiuto')} value={nome} onChange={(e) => setNome(e.target.value)} size="small" fullWidth />

          <Box sx={{ px: 1.5, pt: 1.5, pb: 1, borderRadius: `${tokens.radius.md}px`, bgcolor: tokens.color.bone, minHeight: 84 }}>
            {scalaValida && <ScalaCompenso scaglioni={scaglioni} />}
          </Box>

          {righe.map((r, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <TextField label={t('dialogo_profilo.dal_mese')} value={r.dal} onChange={(e) => cambia(i, 'dal', e.target.value)} size="small" inputProps={{ inputMode: 'numeric' }} sx={{ width: 104 }} />
              <TextField label={t('dialogo_profilo.al_mese')} value={r.perSempre ? '' : r.al} onChange={(e) => cambia(i, 'al', e.target.value)} disabled={r.perSempre} size="small" inputProps={{ inputMode: 'numeric' }} sx={{ width: 104 }} />
              <TextField label={t('dialogo_profilo.percentuale')} value={r.percentuale} onChange={(e) => cambia(i, 'percentuale', e.target.value)} size="small" inputProps={{ inputMode: 'decimal' }} sx={{ width: 112 }} />
              {i === righe.length - 1 && (
                <FormControlLabel
                  control={<Checkbox checked={r.perSempre} onChange={(e) => cambia(i, 'perSempre', e.target.checked)} size="small" />}
                  label={t('dialogo_profilo.per_sempre')}
                />
              )}
              <Box sx={{ flex: 1 }} />
              {righe.length > 1 && (
                <Tooltip title={t('azioni.togli_scaglione')}>
                  <IconButton size="small" onClick={() => setRighe((xs) => xs.filter((_, j) => j !== i))}><Close fontSize="small" /></IconButton>
                </Tooltip>
              )}
            </Stack>
          ))}
          <Box>
            <Button size="small" startIcon={<Add />} onClick={aggiungi} sx={{ textTransform: 'none' }}>{t('azioni.aggiungi_scaglione')}</Button>
          </Box>
          {problemi.length > 0 && (
            <Alert severity="info">
              {problemi.map((p) => t(`problemi_profilo.${p.codice}`, { n: 'indice' in p ? p.indice + 1 : 0 })).join(' ')}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onChiudi} disabled={lavora} sx={{ textTransform: 'none' }}>{t('azioni.annulla')}</Button>
        <Button variant="contained" onClick={salva} disabled={problemi.length > 0 || lavora} startIcon={lavora ? <CircularProgress size={14} /> : undefined} sx={{ textTransform: 'none' }}>
          {t('azioni.salva')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
